import dynamic from 'next/dynamic';
import { useState } from 'react';

import { Link } from '@/components/Link';
import { SkeletonLoader } from '@/components/SkeletonLoader';
import { SecondaryHeading } from '@/components/typography';
import { useWebGL } from '@/hooks/useWebGL';
import { SimpleLocationDto } from '@/lib/locations/dto';
import { getBackgroundSectionClassName } from '@/utils/marketing-landing-page';

import { BackgroundColors } from '../../types';
import LocationCardsLoader from './LocationCardsLoader';

import clsx from 'clsx';

const Map = dynamic(() => import('./Map'), {
  ssr: false,
  loading: () => (
    <SkeletonLoader
      className="h-[240px] w-full"
      dataTestId="location-search-map-loader"
    />
  ),
});
const LocationCard = dynamic(() => import('@/components/cards/LocationCard'), {
  ssr: false,
});

type LocationCardsWithMapProps = {
  mapboxAccessToken: string;
  title?: string;
  locations: SimpleLocationDto[];
  backgroundColor?: BackgroundColors;
  isLoading: boolean;
  cardsListContainerClassName?: string;
  mapHidden?: boolean;
  allLocationsLinkLabel?: string;
};

export function LocationCardsWithMap({
  title,
  locations,
  mapboxAccessToken,
  backgroundColor,
  isLoading,
  cardsListContainerClassName,
  mapHidden,
  allLocationsLinkLabel,
}: LocationCardsWithMapProps) {
  const { isWebGLSupported } = useWebGL();
  const [hoveredMarkerSlug, setHoveredMarkerSlug] = useState<string>();

  return (
    <section className="relative lg:h-[560px]">
      <div
        className={clsx(
          'flex flex-col items-center justify-center h-full lg:items-start pt-4 pb-10 lg:pb-0',
          getBackgroundSectionClassName(backgroundColor)
        )}
      >
        {title && (
          <SecondaryHeading
            className={clsx(
              'self-start mb-4',
              cardsListContainerClassName ?? 'medium-container'
            )}
          >
            {title}
          </SecondaryHeading>
        )}

        {mapboxAccessToken && isWebGLSupported !== false && !mapHidden && (
          <>
            <div className="lg:absolute block inset-y-0 right-0 border-grey-95 h-[240px] w-full lg:w-[55%] lg:h-full z-[1]">
              {isWebGLSupported === undefined || isLoading ? (
                <SkeletonLoader
                  className="h-[240px] lg:h-full"
                  dataTestId="location-search-map-loader"
                />
              ) : (
                <Map
                  mapboxAccessToken={mapboxAccessToken}
                  locations={locations}
                  hoveredMarkerSlug={hoveredMarkerSlug}
                  setHoveredMarkerSlug={setHoveredMarkerSlug}
                />
              )}
            </div>
          </>
        )}
        <div
          className={clsx(
            'flex items-center flex-col lg:flex-row',
            cardsListContainerClassName ?? 'medium-container'
          )}
        >
          <div
            className={clsx(
              'lg:px-0 lg:mt-0',
              !mapHidden && isWebGLSupported && '-mt-8'
            )}
          >
            {isLoading ? (
              <LocationCardsLoader dataTestId="location-cards-with-map-loader" />
            ) : (
              <ul className="flex flex-col gap-5 sm:flex-row z-[1]">
                {locations.map((loc) => (
                  <li
                    className={clsx(
                      'w-full sm:w-[260px] lg:w-[340px] hover:shadow-md',
                      hoveredMarkerSlug === loc.slug && 'shadow-md'
                    )}
                    onMouseEnter={() => setHoveredMarkerSlug(loc.slug)}
                    onMouseLeave={() => setHoveredMarkerSlug(undefined)}
                    key={loc.slug}
                  >
                    <LocationCard location={loc} />
                  </li>
                ))}
              </ul>
            )}

            {allLocationsLinkLabel && (
              <Link
                href="/locations"
                className="flex justify-center mt-4 font-bold text-center underline md:justify-start text-ocean-60"
              >
                {allLocationsLinkLabel}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
